import { ReactElement } from 'react';
import { ProductsAnalyticsProvider } from '@client/analytics/context/products/ProductsAnalyticsContext';
import { SearchAnalyticsProvider } from '@client/analytics/context/search/SearchAnalyticsContext';
import { useRemoteRecordsAnalytics } from '@client/analytics/hooks';
import { analyzeSearchPageView } from '@client/analytics/redux';
import { EVENT_SOURCE } from '@client/analytics/types/types';
import { Page } from '@client/app/components/App/Page';
import { useScrollRestore } from '@client/app/context/ScrollRestoreContext';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { Catalog } from '@client/catalog/components/Catalog';
import { useSearchData, useSearchQuery, useSearchRequestedQuery } from '@client/catalog/hooks';
import { getCrumbsFromCategories } from '@client/catalog/utils/crumbs';
import { useTranslate } from '@client/i18n/hooks';
import { useProductsTiles } from '@client/product/hooks';
import { useUrlCreator } from '@client/routes/hooks';
import { Alert, ALERT_STYLE_TYPE } from '@lib/components/Alert';
import './SearchPage.scss';

export const SearchPage = (): ReactElement => {
  const t = useTranslate();
  const urlCreator = useUrlCreator();
  const catalog = useSearchData();
  const query = useSearchQuery();
  const requestedQuery = useSearchRequestedQuery();
  const productTiles = useProductsTiles();
  const skus = catalog.data?.products.map(product => product.sku) || [];
  const scrollContext = useScrollRestore();
  const title = query ? `"${query}"` : '';

  useRemoteRecordsAnalytics(
    [
      catalog,
      ...skus.map(sku => productTiles[sku]),
    ],
    analyzeSearchPageView,
  );

  const breadcrumbs = catalog.data
    ? [
      ...getCrumbsFromCategories(
        catalog.data?.navigation.selected,
        urlCreator,
      ),
      {
        title: `${t('Search for')} "${query}"`,
      },
    ]
    : [{ title: '' }];

  useBreadcrumbs(breadcrumbs);

  return (
    <ProductsAnalyticsProvider eventSource={EVENT_SOURCE.search}>
      <SearchAnalyticsProvider>
        <Page className='SearchPage' title={title || t('Search')}>
          <Catalog
            title={title}
            isLevelZeroAvailable
            getScrollPosition={scrollContext.getScrollPosition}
          >
            {query !== requestedQuery && (
              <>
                <Alert styleType={ALERT_STYLE_TYPE.info}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'Your search of "{requestedQuery}" returned no results. Instead, we searched for "<b>{query}</b>" for you.',
                        {
                          query,
                          requestedQuery,
                        },
                      ),
                    }}
                  />
                </Alert>
                <br />
              </>
            )}
          </Catalog>
        </Page>
      </SearchAnalyticsProvider>
    </ProductsAnalyticsProvider>
  );
};
